import { createEventMergeContext, submitEventMerge } from '@otto-ec/tracking-bct';
import { triggerHapticFeedback } from '@otto-ec/apps-api/hapticfeedback';
import { eventQBus } from '@otto-ec/global-resources/event-q-bus';
import { submitToExactag } from '@otto-ec/online-marketing/exactag';

class AddToWishlistService {
    constructor(adjustTracking, firebaseTracking, toggles = { toggleActive: () => false }, baseUrl = '/wishlist-view/') {
        this._toggles = toggles;
        this._baseUrl = baseUrl;
        this._eventKeys = {
            add: 'ft1.wishlist.add',
            add_status: 'ft1.wishlist.add_status',
            remove: 'ft1.wishlist.remove'
        };
        this._endpoints = {
            multiUpdate: this._baseUrl + 'items'
        };
        this._adjustTracking = adjustTracking;
        this._firebaseTracking = firebaseTracking;
    }

    init() {
        eventQBus.on(this._eventKeys.add, data => this._add(data));
        eventQBus.on(this._eventKeys.remove, data => this._removeList(data));
        eventQBus.on(this._eventKeys.add_status, data => this._triggerHapticFeedback(data));
    }

    _add(data) {
        return this.addToWishlist(data.variationId, data.qualified, data.addedFrom, data.wishlistId);
    }

    _removeList(data) {
        if (data.variationIds.length > 0) {
            return this.multiRemoveItemsFromWishlist(data.variationIds, data.deletedFrom, data.wishlistId, data.deletionInteractionType);
        }
        return Promise.resolve();
    }

    _executeMultiUpdate(data, callback) {
        const headers = { 'Content-Type': 'application/json' };
        const body = JSON.stringify(data);
        return fetch(this._endpoints.multiUpdate, { headers, body, method: 'POST' }).then(callback);
    }

    async addToWishlist(variationId, qualified, client, wishlistId) {
        if (typeof variationId === 'undefined') {
            throw new Error("Required field 'variationId' missing. No Add2WL will be sent!");
        }
        if (typeof qualified === 'undefined') {
            throw new Error("Required field 'qualified' missing. No Add2WL will be sent!");
        }

        const operation = 'ADD';
        const context = await createEventMergeContext();
        const eventMergeId = context.eventMergeId;
        const data = {
            items: [
                {
                    variationId,
                    qualified
                }
            ],
            client,
            sourceUrl: window.location.pathname + window.location.search,
            wishlistId,
            operation,
            eventMergeId
        };

        const payload = {
            features: [],
            name: 'add'
        };

        submitEventMerge(eventMergeId, {}, payload);

        return this._executeMultiUpdate(data, response => this._handleMultiUpdateResponse(response, true, [variationId], eventMergeId));
    }

    async multiRemoveItemsFromWishlist(variationIds, client, wishlistId, deletionInteractionType) {
        const sourceUrl = window.location.pathname + window.location.search;
        const context = await createEventMergeContext();
        const eventMergeId = context.eventMergeId;
        const items = variationIds.map(variationId => ({ variationId }));
        const operation = 'REMOVE';
        const data = {
            client,
            items,
            sourceUrl,
            eventMergeId,
            wishlistId,
            operation,
            deletionInteractionType: deletionInteractionType
        };

        const payload = {
            features: [],
            name: 'delete'
        };

        submitEventMerge(eventMergeId, {}, payload);

        return this._executeMultiUpdate(data, response => this._handleMultiUpdateResponse(response, false, variationIds, eventMergeId));
    }

    async _handleMultiUpdateResponse(response, isAddOperation, variationIds, eventMergeId) {
        const sendStatusEvent = success => {
            this._sendStatusEvent(variationIds, success, isAddOperation, eventMergeId);
        };
        if (response.status === 200) {
            const responseJson = await response.json();
            if (responseJson.success) {
                this._signalWishlistChanged(responseJson.newAmount);
                this._trackExactag(isAddOperation, variationIds);
                this._trackAdjust(isAddOperation, responseJson.articleNumber, responseJson.variationLink);
                this._trackFirebase(isAddOperation, responseJson.articleNumber);
                sendStatusEvent(true);
            } else {
                this._signalWishlistChangedEmpty();
                sendStatusEvent(false);
            }
        } else {
            this._signalWishlistChangedEmpty();
            sendStatusEvent(false);
        }
    }

    _sendStatusEvent(variationIds, success, isAddOperation, eventMergeId) {
        const topic = isAddOperation ? 'add' : 'remove';
        eventQBus.emit(`ft1.wishlist.${topic}_status`, {
            variationIds,
            success,
            eventMergeId
        });
    }

    _signalWishlistChanged(newAmount) {
        document.dispatchEvent(new CustomEvent('wishlist.changed', { detail: { newAmount } }));
    }

    /**
 *
 *
 */
    _signalWishlistChangedEmpty() {
        document.dispatchEvent(new CustomEvent('wishlist.changed'));
    }

    _trackExactag(isAddOperation, variationIds) {
        if (isAddOperation) {
            variationIds.forEach(variationId => {
                if (isAddOperation) {
                    submitToExactag({
                        pt: 'add_to_wishlist',
                        mv: variationId
                    });
                }
            });
        }
    }

    _trackAdjust(isAddOperation, articleNumber, variationLink) {
        if (isAddOperation) {
            this._adjustTracking.trackAdjust(articleNumber, variationLink);
        }
    }

    _trackFirebase(isAddOperation, articleNumber) {
        if (isAddOperation) {
            this._firebaseTracking.trackFirebase(articleNumber);
        }
    }

    _triggerHapticFeedback(statusResponse) {
        if (statusResponse.success) {
            triggerHapticFeedback();
        }
    }
}

export { AddToWishlistService };
